import { useTranslation } from "react-i18next";

import PageBox from "../../../../components/box/PageBox";
import RegularContainer from "../../../../components/container/RegularContainer";
import { HeadingWithInfo } from "../../../../components/heading";
import { useSocialContext } from "../../context";
import useBarChart from "../../hooks/useBarChart";
import useBarChartDouble from "../../hooks/useBarChartDouble";
import useSocialMessageAmount from "../../hooks/useSocialMessageAmount";
import { SOCIAL_CATEGORY_TYPE } from "../../types/enum";
import { IBarProps } from "../../types/interfaces";
import { getBarColor } from "../../utils";
import ChartContainer from "../chartContainer/ChartContainer";
import ChartsLayout from "../chartsLayout/ChartsLayout";
import styles from "./SocialMessageAmount.module.scss";

const BarChart = ({ xData, yData, category }: IBarProps) => {
    const barColor = getBarColor(category);
    return useBarChart({
        xData,
        yData,
        selectedBarColor: barColor,
        category,
    });
};

const SocialMessageAmount = () => {
    const { category } = useSocialContext();
    const { t } = useTranslation();

    const { facebookAmountData, lineAmountData, allAmountData, webAmountData, kioskAmountData, dateData } =
        useSocialMessageAmount({ category });

    const getSocialData = (category: string) => {
        const mappingObj: Record<string, number[] | undefined> = {
            all: [],
            line: lineAmountData,
            facebook: facebookAmountData,
            web: webAmountData,
            kiosk: kioskAmountData,
        };
        return mappingObj[category] ?? [];
    };

    const selectedAmountData = getSocialData(category);
    const selectedBarColor = getBarColor(category);

    const kioskBarChart = BarChart({ xData: dateData, yData: kioskAmountData, category: SOCIAL_CATEGORY_TYPE.Kiosk });
    const facebookBarChart = BarChart({
        xData: dateData,
        yData: facebookAmountData,
        category: SOCIAL_CATEGORY_TYPE.Facebook,
    });
    const webBarChart = BarChart({ xData: dateData, yData: webAmountData, category: SOCIAL_CATEGORY_TYPE.Web });
    const lineBarChart = BarChart({ xData: dateData, yData: lineAmountData, category: SOCIAL_CATEGORY_TYPE.Line });

    const barChartDouble = useBarChartDouble({
        xBarData: dateData,
        yBarDataPrimary: allAmountData,
        category: category,
        yBarDataSecond: selectedAmountData,
        selectedBarColor: selectedBarColor,
    });

    const barChartDoubleTotal = allAmountData?.reduce((acc, curr) => Number(acc) + Number(curr), 0);
    const webBarChartTotal = webAmountData?.reduce((acc, curr) => Number(acc) + Number(curr), 0);
    const facebookBarChartTotal = facebookAmountData?.reduce((acc, curr) => Number(acc) + Number(curr), 0);
    const lineBarChartTotal = lineAmountData?.reduce((acc, curr) => Number(acc) + Number(curr), 0);
    const kioskBarChartTotal = kioskAmountData?.reduce((acc, curr) => Number(acc) + Number(curr), 0);

    return (
        <RegularContainer sx={{ mt: 0, pt: 0 }}>
            <RegularContainer.Content>
                <PageBox hasChatBox={false}>
                    <ChartsLayout isRwdNone={false}>
                        <div className={styles.chartContainer}>
                            <HeadingWithInfo
                                title={t("ANALYSIS.Number_Message_Social")}
                                info={t("ANALYSIS.Number_Message_Social_HELP")}
                            />
                            <ChartContainer isBigChart={true} title=' '>
                                <div ref={barChartDouble} style={{ height: "100%", width: "100%" }} />
                            </ChartContainer>
                            {barChartDoubleTotal !== 0 && (
                                <div className={styles.total}>
                                    {t("ANALYSIS.Total")}: {barChartDoubleTotal}
                                </div>
                            )}
                        </div>
                    </ChartsLayout>
                    <ChartsLayout isRwdNone={true}>
                        <div className={styles.chartContainer}>
                            <HeadingWithInfo
                                title={t("ANALYSIS.Number_Message_Line")}
                                info={t("ANALYSIS.Number_Message_Line_HELP")}
                            />
                            <ChartContainer isBigChart={false} title=' '>
                                <div ref={lineBarChart} style={{ height: "100%", width: "100%" }} />
                            </ChartContainer>
                            {lineBarChartTotal !== 0 && (
                                <div className={styles.total}>
                                    {t("ANALYSIS.Total")}: {lineBarChartTotal}
                                </div>
                            )}
                        </div>
                        <div className={styles.chartContainer}>
                            <HeadingWithInfo
                                title={t("ANALYSIS.Number_Message_Web")}
                                info={t("ANALYSIS.Number_Message_Web_HELP")}
                            />
                            <ChartContainer isBigChart={false} title=' '>
                                <div ref={webBarChart} style={{ height: "100%", width: "100%" }} />
                            </ChartContainer>
                            {webBarChartTotal !== 0 && (
                                <div className={styles.total}>
                                    {t("ANALYSIS.Total")}: {webBarChartTotal}
                                </div>
                            )}
                        </div>
                    </ChartsLayout>
                    <ChartsLayout isRwdNone={true} isLastMB0={true}>
                        <div className={styles.chartContainer}>
                            <HeadingWithInfo
                                title={t("ANALYSIS.Number_Message_Facebook")}
                                info={t("ANALYSIS.Number_Message_Facebook_HELP")}
                            />
                            <ChartContainer isBigChart={false} title=' '>
                                <div ref={facebookBarChart} style={{ height: "100%", width: "100%" }} />
                            </ChartContainer>
                            {facebookBarChartTotal !== 0 && (
                                <div className={styles.total}>
                                    {t("ANALYSIS.Total")}: {facebookBarChartTotal}
                                </div>
                            )}
                        </div>
                        <div className={styles.chartContainer}>
                            <HeadingWithInfo
                                title={t("ANALYSIS.Number_Message_Kiosk")}
                                info={t("ANALYSIS.Number_Message_Kiosk_HELP")}
                            />
                            <ChartContainer isBigChart={false} title=' '>
                                <div ref={kioskBarChart} style={{ height: "100%", width: "100%" }} />
                            </ChartContainer>
                            {kioskBarChartTotal !== 0 && (
                                <div className={styles.total}>
                                    {t("ANALYSIS.Total")}: {kioskBarChartTotal}
                                </div>
                            )}
                        </div>
                    </ChartsLayout>
                </PageBox>
            </RegularContainer.Content>
        </RegularContainer>
    );
};
export default SocialMessageAmount;
